import Link from "@ui-kit/navigation/Link/Link.web";
import CartPaymentMethods from "@app-contexts/public/features/basket/components/cart-summary/CartPaymentMethods/CartPaymentMethods.web";
import { useCartSummaryData } from "@app-contexts/public/features/basket/hooks/useCartSummaryData";
import CartTimer from "@shared/components/application/data-display/CartTimer/CartTimer.web";
import CheckoutLabel from "@shared/components/application/data-display/CheckoutLabel/CheckoutLabel.web";
import useWindowSize from "@shared/hooks/useWindowSize";
import { PUBLIC_ROUTES } from "@app-contexts/public/constants/public-routes";
import breakpoints from "@shared/theme/props/breakpoints";
import { useTranslation } from "@shared/utils/internationalization";
import Divider from "@ui-kit/data-display/Divider/Divider.web";
import Button from "@ui-kit/form/Button/Button.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Heading from "@ui-kit/typography/Heading/Heading.web";
import { useMemo } from "react";
import CartItem from "../cart-summary-items/cart-item/CartItem/CartItem.web";
import { CartSummaryProps } from "./CartSummary.props";

const { sm } = breakpoints;

const CartSummary = ({
  venue,
  holdLink = undefined,
  isDrawer,
  onCartClose = undefined,
  dynamicHoldParams = undefined,
}: CartSummaryProps) => {
  const { t } = useTranslation("purchases");
  const checkoutUrl = PUBLIC_ROUTES.CHECKOUT.ROOT;

  const {
    hasItems,
    displayItemGroups,
    holdDetailsData,
    basketUpgradesData,
    isBasketUpdating,
    basketItemData,
    supportedCCTypes,
    totalItemQuantity,
    isWaitlist,
  } = useCartSummaryData({
    enableItemData: true,
    holdSlug: holdLink,
    dynamicHoldParams,
  });

  const { width: windowWidth } = useWindowSize();
  const isSmallerThanSm = useMemo(
    () => windowWidth && windowWidth < sm - 1,
    [windowWidth]
  );

  const disableCheckout = !hasItems;

  return (
    <Flex
      flexDirection="column"
      {...(isDrawer
        ? { height: "100%" }
        : // 445px is the height of the header, used to prevent cart summary from extending below page.
          { maxHeight: "calc(100vh - 445px)" })}
      overflow="hidden"
      marginInlineStart="none"
    >
      <Box flex="1" overflowY="auto" minHeight="none" overflowX="hidden">
        {!isDrawer && (
          <Heading size="2xl" color="fg">
            {t("Cart")}
          </Heading>
        )}

        {!isSmallerThanSm && (
          <Box my="xs">
            <CartTimer.Text />
          </Box>
        )}

        <Box w="full" mt="xs" mb="xl">
          {displayItemGroups.map((item) => (
            <CartItem
              key={item.id}
              item={item}
              disabled={isBasketUpdating}
              venue={venue}
              event={basketItemData.eventsMapping[item.event]}
              product={basketItemData.productsMapping[item.product]}
              ticketType={
                basketItemData.ticketTypesMapping[`${item.event}:${item.type}`]
              }
              productAttribute={
                basketItemData.productsAttributesMapping[
                  `${item.product}:${item.product_attribute}`
                ]
              }
              membershipLevel={
                basketItemData.membershipLevelsMapping[
                  `${item.membership_group}:${item.membership_level}`
                ]
              }
              onCartClose={onCartClose}
              holdPurchaseLimits={
                holdDetailsData?.on_hold_detail?.purchase_limit_min != null &&
                holdDetailsData?.on_hold_detail?.purchase_limit_max != null
                  ? {
                      min: holdDetailsData.on_hold_detail.purchase_limit_min,
                      max: holdDetailsData.on_hold_detail.purchase_limit_max,
                    }
                  : undefined
              }
              itemGroupUpgrades={
                // Dynamic hold baskets enforce a strict item-group whitelist on the backend
                // (DynamicHoldValidator._set_hold_item_groups_and_limits). Only ticket types,
                // products, and memberships explicitly present in the hold's own inventory are
                // allowed — anything else raises TVEM_UNFULFILLABLE_LINE_ITEM. Upgrade offers
                // would add items outside that whitelist, so we suppress them here.
                // Regular (non-dynamic) holds have no such whitelist restriction.
                !dynamicHoldParams && item.id
                  ? basketUpgradesData?.[item.id]
                  : undefined
              }
            />
          ))}
        </Box>
      </Box>

      {!isDrawer && (
        <Box flexShrink={0}>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <Link href={disableCheckout ? "#" : checkoutUrl} asChild>
            <Button
              as="span"
              data-testid="detail-page-cart-checkout"
              colorPalette="primary"
              variant="solid"
              width="100%"
              minHeight="element.3xl"
              disabled={disableCheckout || isBasketUpdating}
              isLoading={isBasketUpdating}
              mt="lg"
            >
              <CheckoutLabel
                totalItemQuantity={totalItemQuantity}
                isWaitlist={isWaitlist}
              />
            </Button>
          </Link>

          {supportedCCTypes && supportedCCTypes.length > 0 && (
            <>
              <Divider my="lg" />
              <Box w="full">
                <CartPaymentMethods supportedCardTypes={supportedCCTypes} />
              </Box>
            </>
          )}
        </Box>
      )}
    </Flex>
  );
};

export default CartSummary;
